import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { DataStateRow } from "@/components/ui/data-state";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, Trash2, Download, Users as UsersIcon, CheckCircle2,
  Mail, CalendarPlus, ArrowUpDown, ChevronLeft, ChevronRight, Filter, X,
  Phone, Building2, MapPin, Banknote, CalendarDays, BadgeCheck, UserX, RotateCcw,
  IdCard, Landmark, Cake, FileSignature, AlertTriangle, Clock4,
} from "lucide-react";
import { validateEmployee } from "@/lib/business-validation";

const PAGE_SIZE = 50;

const ROLES = [
  { v: "manager", l: "Quản lý" },
  { v: "sales", l: "Bán hàng" },
  { v: "restaurant", l: "Phục vụ NH" },
  { v: "kitchen", l: "Bếp" },
  { v: "warehouse", l: "Kho" },
  { v: "hr", l: "HR" },
  { v: "finance", l: "Kế toán" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.v, r.l]));

const LEVELS = [
  { v: "intern", l: "Thực tập" },
  { v: "junior", l: "Junior" },
  { v: "middle", l: "Middle" },
  { v: "senior", l: "Senior" },
  { v: "lead", l: "Lead" },
  { v: "manager", l: "Manager" },
];

const WORK_STATUSES = [
  { v: "active", l: "Đang làm", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { v: "probation", l: "Thử việc", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  { v: "maternity", l: "Thai sản", cls: "bg-pink-50 text-pink-800 border-pink-200" },
  { v: "leave", l: "Nghỉ phép dài", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { v: "suspended", l: "Tạm hoãn", cls: "bg-stone-100 text-stone-800 border-stone-300" },
  { v: "resigned", l: "Đã nghỉ", cls: "bg-stone-100 text-stone-600 border-stone-200" },
];
const WORK_STATUS_LABEL = Object.fromEntries(WORK_STATUSES.map((s) => [s.v, s]));

const BANKS = [
  "Vietcombank", "Techcombank", "MB Bank", "BIDV", "Agribank", "VietinBank",
  "ACB", "VPBank", "Sacombank", "TPBank", "VIB", "HDBank", "OCB", "MSB",
];

const GENDER_OPTIONS = [
  { v: "male", l: "Nam" },
  { v: "female", l: "Nữ" },
  { v: "other", l: "Khác" },
];

const empty = {
  code: "",
  full_name: "",
  phone: "",
  email: "",
  role: "",
  position: "",
  company_id: "",
  branch_id: "",
  base_salary: 0,
  hire_date: "",
  dob: "",
  gender: "",
  cccd: "",
  cccd_issue_date: "",
  cccd_issue_place: "",
  address: "",
  bank_name: "",
  bank_account: "",
  bank_holder: "",
  level: "",
  department: "",
  manager_id: "",
  work_status: "active",
  note: "",
};

const STATUS_CHIPS = [
  { v: "active", l: "Đang làm" },
  { v: "deleted", l: "Đã nghỉ" },
  { v: "all", l: "Tất cả" },
];

const AVATAR_COLORS = [
  "bg-emerald-700",
  "bg-amber-600",
  "bg-sky-700",
  "bg-rose-700",
  "bg-violet-700",
  "bg-stone-700",
  "bg-teal-700",
  "bg-orange-700",
];

const avatarColor = (s) => {
  const k = String(s || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[k % AVATAR_COLORS.length];
};
const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?";

const tenureText = (hire) => {
  if (!hire) return "—";
  const d = new Date(hire);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) return "Sắp vào";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem} tháng`;
  if (rem === 0) return `${years} năm`;
  return `${years} năm ${rem} th`;
};

const toCsv = (rows, headers) => {
  const esc = (s) => {
    const v = s == null ? "" : String(s);
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => esc(h.get(r))).join(",")).join("\n");
  return `﻿${head}\n${body}`;
};

const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function Employees() {
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("active"); // active | deleted | all
  const [roleFilter, setRoleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [sort, setSort] = useState({ key: "code", dir: "asc" });
  const [selected, setSelected] = useState(() => new Set());

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [formTab, setFormTab] = useState("personal");
  const [editId, setEditId] = useState(null);
  const [editVersion, setEditVersion] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [detail, setDetail] = useState(null); // selected employee object

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      const cleanQ = q.trim();
      if (cleanQ) params.q = cleanQ;
      if (status !== "active") params.include_deleted = true;
      const r = await api.get("/employees", { params });
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      setLoadError(parseApiError(e, "Không tải được danh sách nhân sự").message);
    } finally {
      setLoading(false);
    }
  }, [q, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [q, status]);

  const filtered = useMemo(() => {
    let arr = data;
    if (status === "active") arr = arr.filter((e) => !e.deleted_at);
    else if (status === "deleted") arr = arr.filter((e) => Boolean(e.deleted_at));
    if (roleFilter) arr = arr.filter((e) => e.role === roleFilter);
    if (companyFilter) arr = arr.filter((e) => e.company_id === companyFilter);
    if (branchFilter) arr = arr.filter((e) => e.branch_id === branchFilter);

    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const av = a[key];
      const bv = b[key];
      if (key === "base_salary") return ((Number(av) || 0) - (Number(bv) || 0)) * sgn;
      if (key === "hire_date") {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return (at - bt) * sgn;
      }
      return String(av || "").localeCompare(String(bv || ""), "vi") * sgn;
    };
    return [...arr].sort(cmp);
  }, [data, status, roleFilter, companyFilter, branchFilter, sort]);

  // Tenant-wide stats independent of pagination (fetched from /employees/stats)
  const [serverStats, setServerStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/employees/stats")
      .then((r) => {
        if (!cancelled) setServerStats(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [data]); // refetch after list refresh so create/delete updates the cards

  const stats = useMemo(() => {
    if (serverStats) {
      return {
        total: serverStats.total || 0,
        active: serverStats.active || 0,
        deleted: serverStats.deleted || 0,
        withEmail: serverStats.with_email || 0,
        newThisMonth: serverStats.new_this_month || 0,
      };
    }
    // Fallback: derive from current page (correct on small tenants only)
    const all = data;
    const active = all.filter((e) => !e.deleted_at);
    const withEmail = active.filter((e) => Boolean(e.email)).length;
    const now = new Date();
    const newThisMonth = active.filter((e) => {
      if (!e.hire_date) return false;
      const d = new Date(e.hire_date);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
    return {
      total: all.length,
      active: active.length,
      deleted: all.length - active.length,
      withEmail,
      newThisMonth,
    };
  }, [data, serverStats]);

  const openCreate = () => {
    setForm(empty);
    setEditId(null);
    setEditVersion(null);
    setFieldErrors({});
    setFormTab("personal");
    setOpen(true);
  };
  const openEdit = (e) => {
    setForm({
      ...empty,
      ...e,
      base_salary: e.base_salary ?? 0,
      hire_date: e.hire_date?.slice(0, 10) || "",
      dob: e.dob?.slice(0, 10) || "",
      cccd_issue_date: e.cccd_issue_date?.slice(0, 10) || "",
      work_status: e.work_status || "active",
      manager_id: e.manager_id || "",
    });
    setEditId(e.id);
    setEditVersion(e.version ?? null);
    setFieldErrors({});
    setFormTab("personal");
    setOpen(true);
  };

  const managerOptions = useMemo(
    () => data.filter((e) => !e.deleted_at && e.id !== editId).slice(0, 200),
    [data, editId],
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_salary: Number(form.base_salary || 0),
        company_id: form.company_id || null,
        branch_id: form.branch_id || null,
        email: form.email || null,
        phone: form.phone || null,
        role: form.role || null,
        position: form.position || null,
        hire_date: form.hire_date || null,
        dob: form.dob || null,
        gender: form.gender || null,
        cccd: form.cccd || null,
        cccd_issue_date: form.cccd_issue_date || null,
        cccd_issue_place: form.cccd_issue_place || null,
        address: form.address || null,
        bank_name: form.bank_name || null,
        bank_account: form.bank_account || null,
        bank_holder: form.bank_holder || null,
        level: form.level || null,
        department: form.department || null,
        manager_id: form.manager_id || null,
        work_status: form.work_status || "active",
        note: form.note || null,
        expected_version: editId ? editVersion : null,
      };
      const validation = validateEmployee(payload);
      if (!validation.ok) {
        setFieldErrors(validation.fieldErrors);
        toast.error(validation.message);
        const firstErr = Object.keys(validation.fieldErrors)[0];
        if (["code", "full_name", "phone", "email", "dob", "gender", "address"].includes(firstErr)) setFormTab("personal");
        else if (["cccd", "cccd_issue_date", "cccd_issue_place", "bank_name", "bank_account"].includes(firstErr)) setFormTab("legal");
        else if (["role", "level", "position", "department", "company_id", "branch_id", "manager_id", "work_status"].includes(firstErr)) setFormTab("position");
        else setFormTab("compensation");
        return;
      }
      if (editId) await api.put(`/employees/${editId}`, payload);
      else await api.post("/employees", payload);
      toast.success(editId ? "Đã cập nhật nhân viên" : "Đã thêm nhân viên mới");
      setOpen(false);
      load();
    } catch (e) {
      const parsed = parseApiError(e, "Lưu thất bại");
      setFieldErrors(parsed.fieldErrors);
      toast.error(parsed.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Đã chuyển sang trạng thái đã nghỉ");
      if (detail?.id === id) setDetail(null);
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Xóa thất bại").message);
    }
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((errors) => ({ ...errors, [k]: undefined }));
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));
  const someVisibleSelected = filtered.some((e) => selected.has(e.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((e) => next.delete(e.id));
      else filtered.forEach((e) => next.add(e.id));
      return next;
    });
  };

  const exportCsv = (rows, name) => {
    if (!rows.length) {
      toast.info("Không có dữ liệu để xuất");
      return;
    }
    const headers = [
      { label: "Mã NV", get: (r) => r.code },
      { label: "Họ tên", get: (r) => r.full_name },
      { label: "Email", get: (r) => r.email || "" },
      { label: "SĐT", get: (r) => r.phone || "" },
      { label: "Vai trò", get: (r) => ROLE_LABEL[r.role] || r.role || "" },
      { label: "Chức vụ", get: (r) => r.position || "" },
      { label: "Pháp nhân", get: (r) => r.company_name || "" },
      { label: "Chi nhánh", get: (r) => r.branch_name || "" },
      { label: "Lương cơ bản", get: (r) => r.base_salary ?? 0 },
      { label: "Ngày vào", get: (r) => (r.hire_date ? r.hire_date.slice(0, 10) : "") },
      { label: "Trạng thái", get: (r) => (r.deleted_at ? "Đã nghỉ" : "Đang làm") },
    ];
    downloadCsv(`${name}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows, headers));
    toast.success(`Đã xuất ${rows.length} bản ghi`);
  };

  const bulkDeletable = useMemo(
    () => filtered.filter((e) => selected.has(e.id) && !e.deleted_at),
    [filtered, selected],
  );

  const bulkRemove = async () => {
    if (!bulkDeletable.length) return;
    let success = 0;
    for (const e of bulkDeletable) {
      try {
        await api.delete(`/employees/${e.id}`);
        success += 1;
      } catch (_) { /* continue */ }
    }
    toast.success(`Đã xóa ${success}/${bulkDeletable.length} nhân viên`);
    setSelected(new Set());
    load();
  };

  const clearFilters = () => {
    setQ("");
    setRoleFilter("");
    setCompanyFilter("");
    setBranchFilter("");
  };

  const hasPreviousPage = page > 0;
  const hasNextPage = data.length === PAGE_SIZE;
  const hasFilters = Boolean(q || roleFilter || companyFilter || branchFilter);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <div data-testid="page-employees">
      <PageHeader
        kicker="Tổ chức"
        title="Nhân sự"
        breadcrumb={["Tổ chức", "Nhân sự"]}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => exportCsv(filtered, "nhansu")}
              className="rounded-sm"
              data-testid="btn-export-employees"
            >
              <Download className="w-4 h-4 mr-1.5" /> Xuất CSV
            </Button>
            <Button
              onClick={openCreate}
              className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
              data-testid="btn-create-employee"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Thêm nhân viên
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<UsersIcon className="w-4 h-4" />}
            label="Tổng nhân sự"
            value={stats.total}
            sub={`${stats.active} đang làm · ${stats.deleted} đã nghỉ`}
            tone="stone"
          />
          <StatCard
            icon={<CheckCircle2 className="w-4 h-4" />}
            label="Đang làm việc"
            value={stats.active}
            sub={stats.total > 0 ? `${Math.round((stats.active / stats.total) * 100)}% tổng số` : "—"}
            tone="emerald"
          />
          <StatCard
            icon={<Mail className="w-4 h-4" />}
            label="Có email công ty"
            value={stats.withEmail}
            sub={stats.active > 0 ? `${Math.round((stats.withEmail / Math.max(stats.active, 1)) * 100)}% NV đang làm` : "—"}
            tone="sky"
          />
          <StatCard
            icon={<CalendarPlus className="w-4 h-4" />}
            label="Vào tháng này"
            value={stats.newThisMonth}
            sub="Nhân viên mới"
            tone="amber"
          />
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.v}
              onClick={() => setStatus(c.v)}
              data-testid={`chip-status-${c.v}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-sm border transition-colors ${
                status === c.v
                  ? "bg-emerald-900 text-white border-emerald-900"
                  : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
              }`}
            >
              {c.l}
              <span className={`ml-1.5 font-mono ${status === c.v ? "text-emerald-200" : "text-stone-400"}`}>
                {c.v === "active" ? stats.active : c.v === "deleted" ? stats.deleted : stats.total}
              </span>
            </button>
          ))}
          <div className="ml-auto text-xs text-stone-500 font-mono">
            Hiển thị {filtered.length} · Trang {page + 1}
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Tìm theo tên, mã NV, SĐT..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 h-9 rounded-sm"
                data-testid="employee-search"
              />
            </div>
            <Select value={roleFilter || "_all"} onValueChange={(v) => setRoleFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm w-[150px] text-xs" data-testid="filter-role">
                <SelectValue placeholder="Vai trò" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Mọi vai trò</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r.v} value={r.v}>
                    {r.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={companyFilter || "_all"} onValueChange={(v) => {
              setCompanyFilter(v === "_all" ? "" : v);
              setBranchFilter("");
            }}>
              <SelectTrigger className="h-9 rounded-sm w-[170px] text-xs" data-testid="filter-company">
                <SelectValue placeholder="Pháp nhân" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Mọi pháp nhân</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={branchFilter || "_all"} onValueChange={(v) => setBranchFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm w-[170px] text-xs" data-testid="filter-branch">
                <SelectValue placeholder="Chi nhánh" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Mọi chi nhánh</SelectItem>
                {branches
                  .filter((b) => !companyFilter || b.company_id === companyFilter)
                  .map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="rounded-sm h-9 text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Xóa lọc
              </Button>
            )}
          </div>

          {/* Bulk bar */}
          {selected.size > 0 && (
            <div
              data-testid="bulk-bar"
              className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-3 text-sm"
            >
              <BadgeCheck className="w-4 h-4 text-emerald-800" />
              <span className="font-semibold text-emerald-900">{selected.size} đã chọn</span>
              <button
                onClick={() => exportCsv(filtered.filter((e) => selected.has(e.id)), "nhansu-chon")}
                className="text-emerald-900 hover:underline font-semibold text-xs"
                data-testid="bulk-export"
              >
                Xuất CSV
              </button>
              {bulkDeletable.length > 0 && (
                <DestructiveConfirmDialog
                  title={`Xóa ${bulkDeletable.length} nhân viên?`}
                  description="Các nhân viên đã chọn sẽ được chuyển sang trạng thái đã nghỉ. Bạn có thể khôi phục bằng cách liên hệ quản trị viên."
                  confirmLabel="Xóa"
                  token={`bulk-${bulkDeletable.length}`}
                  onConfirm={bulkRemove}
                  trigger={
                    <button className="text-red-700 hover:underline font-semibold text-xs" data-testid="bulk-delete">
                      Xóa hàng loạt
                    </button>
                  }
                />
              )}
              <button
                onClick={() => setSelected(new Set())}
                className="ml-auto text-stone-500 hover:text-stone-700 text-xs"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-3 py-2.5 w-9">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Chọn tất cả"
                      data-testid="select-all"
                      className={someVisibleSelected && !allVisibleSelected ? "data-[state=unchecked]:bg-emerald-100" : ""}
                    />
                  </th>
                  <SortableTh sort={sort} k="full_name" onClick={toggleSort}>Nhân viên</SortableTh>
                  <SortableTh sort={sort} k="code" onClick={toggleSort}>Mã NV</SortableTh>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">
                    Vai trò · Chức vụ
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">
                    Chi nhánh
                  </th>
                  <SortableTh sort={sort} k="base_salary" onClick={toggleSort} align="right">
                    Lương cơ bản
                  </SortableTh>
                  <SortableTh sort={sort} k="hire_date" onClick={toggleSort}>Thâm niên</SortableTh>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">
                    Trạng thái
                  </th>
                  <th className="px-4 py-2.5 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {!loading && !loadError && filtered.map((e) => (
                  <tr
                    key={e.id}
                    className={`hover:bg-stone-50 cursor-pointer ${selected.has(e.id) ? "bg-emerald-50/40" : ""}`}
                    data-testid={`row-emp-${e.code}`}
                    onClick={(ev) => {
                      if (ev.target.closest("button,input,[role=checkbox],a")) return;
                      setDetail(e);
                    }}
                  >
                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(e.id)}
                        onCheckedChange={() => toggleSelected(e.id)}
                        aria-label={`Chọn ${e.full_name}`}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 ${avatarColor(e.full_name)}`}
                        >
                          {initials(e.full_name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-stone-900 truncate">{e.full_name}</div>
                          <div className="text-[11px] text-stone-500 truncate font-mono">
                            {e.email || e.phone || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-700">{e.code}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        {e.role && (
                          <Badge variant="secondary" className="w-fit rounded-sm font-bold text-[10px] uppercase tracking-wider">
                            {ROLE_LABEL[e.role] || e.role}
                          </Badge>
                        )}
                        <span className="text-xs text-stone-600">{e.position || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-stone-600 text-xs">
                      <div className="font-semibold text-stone-800">{e.branch_name || "—"}</div>
                      <div className="text-[11px] text-stone-500">{e.company_name || ""}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-right text-stone-800 font-semibold">
                      {fmtMoney(e.base_salary)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-xs text-stone-800 font-semibold">{tenureText(e.hire_date)}</div>
                      <div className="text-[11px] text-stone-500 font-mono">{e.hire_date ? fmtDate(e.hire_date) : "—"}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge deleted={Boolean(e.deleted_at)} workStatus={e.work_status} />
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      <button
                        onClick={() => openEdit(e)}
                        className="text-stone-600 hover:text-emerald-800 mr-2"
                        title="Sửa"
                        data-testid={`edit-${e.code}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {!e.deleted_at && (
                        <DestructiveConfirmDialog
                          title="Xóa nhân viên?"
                          description={`Nhân viên ${e.full_name || e.code || e.id} sẽ bị xóa khỏi hệ thống.`}
                          confirmLabel="Xóa nhân viên"
                          token={String(e.code || e.full_name || e.id)}
                          onConfirm={() => remove(e.id)}
                          trigger={
                            <button className="text-stone-600 hover:text-red-700" title="Xóa" data-testid={`del-${e.code}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))}
                {loading && <DataStateRow colSpan={9} type="loading" message="Đang tải danh sách nhân sự..." />}
                {!loading && loadError && <DataStateRow colSpan={9} type="error" message={loadError} onRetry={load} />}
                {!loading && !loadError && filtered.length === 0 && (
                  <DataStateRow
                    colSpan={9}
                    type="empty"
                    message={hasFilters ? "Không có kết quả phù hợp bộ lọc" : "Chưa có nhân sự nào"}
                  />
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-stone-200 flex items-center justify-between gap-2">
            <div className="text-xs text-stone-500">
              Trang {page + 1} · {data.length} bản ghi tải về · {filtered.length} hiển thị
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage || loading}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                className="rounded-sm"
                data-testid="employees-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Trước
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNextPage || loading}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-sm"
                data-testid="employees-next-page"
              >
                Sau <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editId ? "Sửa nhân viên" : "Thêm nhân viên mới"}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={formTab} onValueChange={setFormTab}>
            <TabsList className="w-full justify-start rounded-sm bg-stone-100 p-1">
              <TabsTrigger value="personal" className="rounded-sm" data-testid="tab-personal">
                1. Cá nhân
              </TabsTrigger>
              <TabsTrigger value="legal" className="rounded-sm" data-testid="tab-legal">
                2. CCCD & NH
              </TabsTrigger>
              <TabsTrigger value="position" className="rounded-sm" data-testid="tab-position">
                3. Vị trí
              </TabsTrigger>
              <TabsTrigger value="compensation" className="rounded-sm" data-testid="tab-compensation">
                4. Lương & HĐ
              </TabsTrigger>
            </TabsList>

            <TabsContent value="personal" className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mã NV *" v={form.code} error={fieldErrors.code} onChange={(v) => set("code", v)} testid="f-code" />
                <Field
                  label="Họ tên *"
                  v={form.full_name}
                  error={fieldErrors.full_name}
                  onChange={(v) => set("full_name", v)}
                  testid="f-name"
                />
                <Field label="SĐT" v={form.phone} onChange={(v) => set("phone", v)} placeholder="0xxx xxx xxx" />
                <Field
                  label="Email công ty"
                  v={form.email}
                  error={fieldErrors.email}
                  onChange={(v) => set("email", v)}
                  placeholder="ten@congty.vn"
                />
                <Field
                  label="Ngày sinh"
                  type="date"
                  v={form.dob}
                  onChange={(v) => set("dob", v)}
                />
                <div>
                  <Label className="text-xs">Giới tính</Label>
                  <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn" /></SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Địa chỉ thường trú</Label>
                  <Input
                    value={form.address || ""}
                    onChange={(e) => set("address", e.target.value)}
                    placeholder="Số nhà, đường, phường, quận, tỉnh"
                    className="h-9 rounded-sm mt-1"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="legal" className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Số CCCD" v={form.cccd} onChange={(v) => set("cccd", v)} placeholder="12 chữ số" />
                <Field label="Ngày cấp CCCD" type="date" v={form.cccd_issue_date} onChange={(v) => set("cccd_issue_date", v)} />
                <div className="col-span-2">
                  <Label className="text-xs">Nơi cấp</Label>
                  <Input
                    value={form.cccd_issue_place || ""}
                    onChange={(e) => set("cccd_issue_place", e.target.value)}
                    placeholder="VD: Cục CSQLHC về TTXH"
                    className="h-9 rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ngân hàng nhận lương</Label>
                  <Select value={form.bank_name || undefined} onValueChange={(v) => set("bank_name", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn ngân hàng" /></SelectTrigger>
                    <SelectContent>
                      {BANKS.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Số tài khoản" v={form.bank_account} onChange={(v) => set("bank_account", v)} placeholder="STK" />
                <Field
                  label="Tên chủ TK (nếu khác)"
                  v={form.bank_holder}
                  onChange={(v) => set("bank_holder", v)}
                  placeholder="Để trống nếu trùng họ tên"
                />
              </div>
            </TabsContent>

            <TabsContent value="position" className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Vai trò</Label>
                  <Select value={form.role || undefined} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="f-role">
                      <SelectValue placeholder="Chọn" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cấp bậc (Level)</Label>
                  <Select value={form.level || undefined} onValueChange={(v) => set("level", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn" /></SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((r) => (
                        <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field
                  label="Chức vụ (Job title)"
                  v={form.position}
                  onChange={(v) => set("position", v)}
                  placeholder="VD: Tổ trưởng ca tối"
                />
                <Field
                  label="Phòng ban / Bộ phận"
                  v={form.department}
                  onChange={(v) => set("department", v)}
                  placeholder="VD: Phòng kỹ thuật"
                />
                <div>
                  <Label className="text-xs">Pháp nhân</Label>
                  <Select
                    value={form.company_id || undefined}
                    onValueChange={(v) => {
                      set("company_id", v);
                      set("branch_id", "");
                    }}
                  >
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="f-company">
                      <SelectValue placeholder="Chọn" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Chi nhánh</Label>
                  <Select value={form.branch_id || undefined} onValueChange={(v) => set("branch_id", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="f-branch">
                      <SelectValue placeholder="Chọn" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches
                        .filter((b) => !form.company_id || b.company_id === form.company_id)
                        .map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Quản lý trực tiếp</Label>
                  <Select value={form.manager_id || "_none"} onValueChange={(v) => set("manager_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn (tuỳ chọn)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Không có —</SelectItem>
                      {managerOptions.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.full_name} ({m.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Trạng thái làm việc</Label>
                  <Select value={form.work_status || "active"} onValueChange={(v) => set("work_status", v)}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WORK_STATUSES.map((s) => (
                        <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="compensation" className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Lương cơ bản (VNĐ)"
                  type="number"
                  v={form.base_salary}
                  error={fieldErrors.base_salary}
                  onChange={(v) => set("base_salary", v)}
                  testid="f-salary"
                />
                <Field
                  label="Ngày vào"
                  type="date"
                  v={form.hire_date}
                  error={fieldErrors.hire_date}
                  onChange={(v) => set("hire_date", v)}
                  testid="f-hire-date"
                />
                <div className="col-span-2">
                  <Label className="text-xs">Ghi chú</Label>
                  <Textarea
                    value={form.note || ""}
                    onChange={(e) => set("note", e.target.value)}
                    rows={2}
                    placeholder="VD: NV onsite VP Nhật, cần laptop riêng..."
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
              {Number(form.base_salary) > 0 && (
                <div className="mt-3 p-3 bg-stone-50 border border-stone-200 rounded-sm text-xs text-stone-600 flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-700" />
                  Lương cơ bản dự kiến: <strong className="text-stone-900 font-mono">{fmtMoney(form.base_salary)}</strong>
                  <span className="text-stone-400">·</span>
                  Năm: <strong className="text-stone-900 font-mono">{fmtMoney(Number(form.base_salary) * 12)}</strong>
                </div>
              )}
              {!editId && (
                <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-sm text-xs text-sky-900 flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-sky-700" />
                  Sau khi lưu, vào màn "Hợp đồng" để tạo HĐ chi tiết (loại, thời hạn, phụ cấp, BHXH...).
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
              Hủy
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
              data-testid="btn-save-employee"
            >
              {saving ? "Đang lưu..." : editId ? "Cập nhật" : "Thêm mới"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
          {detail && (
            <EmployeeDetail
              emp={detail}
              onEdit={() => {
                openEdit(detail);
                setDetail(null);
              }}
              onDelete={() => remove(detail.id)}
              onClose={() => setDetail(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone }) {
  const toneClass =
    tone === "emerald"
      ? "border-l-emerald-600"
      : tone === "sky"
      ? "border-l-sky-600"
      : tone === "amber"
      ? "border-l-amber-500"
      : "border-l-stone-400";
  const iconBg =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700"
      : "bg-stone-100 text-stone-700";
  return (
    <div className={`bg-white border border-stone-200 border-l-4 ${toneClass} rounded-sm px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">{label}</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iconBg}`}>{icon}</div>
      </div>
      <div className="font-serif text-3xl font-bold text-stone-900 mt-1 tracking-tight">{value}</div>
      <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>
    </div>
  );
}

function StatusBadge({ deleted, workStatus }) {
  if (deleted) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded-sm text-[10px] font-bold uppercase tracking-wider">
        <UserX className="w-3 h-3" /> Đã nghỉ
      </span>
    );
  }
  const def = WORK_STATUS_LABEL[workStatus] || WORK_STATUS_LABEL.active;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[10px] font-bold uppercase tracking-wider ${def.cls}`}>
      <BadgeCheck className="w-3 h-3" /> {def.l}
    </span>
  );
}

function SortableTh({ children, k, sort, onClick, align = "left" }) {
  const active = sort.key === k;
  return (
    <th
      className={`text-${align} px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold cursor-pointer select-none hover:text-stone-900`}
      onClick={() => onClick(k)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${active ? "text-emerald-800" : "text-stone-300"}`} />
        {active && <span className="font-mono text-[9px] text-emerald-800">{sort.dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}

function Field({ label, v, onChange, type = "text", testid, error, placeholder }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        placeholder={placeholder}
        className={`h-9 rounded-sm mt-1 ${error ? "border-red-400 focus-visible:ring-red-500" : ""}`}
        data-testid={testid}
      />
      {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
    </div>
  );
}

function EmployeeDetail({ emp, onEdit, onDelete, onClose }) {
  const [tab, setTab] = useState("info");
  const [contracts, setContracts] = useState(null);
  const [contractsErr, setContractsErr] = useState("");
  const deleted = Boolean(emp.deleted_at);
  const workStatus = WORK_STATUS_LABEL[emp.work_status] || (deleted ? WORK_STATUS_LABEL.resigned : WORK_STATUS_LABEL.active);

  useEffect(() => {
    if (tab !== "contracts" || contracts !== null) return;
    let alive = true;
    api
      .get(`/employees/${emp.id}/contracts`)
      .then((r) => alive && setContracts(r.data))
      .catch((e) => alive && setContractsErr(parseApiError(e, "Không tải được hợp đồng").message));
    return () => {
      alive = false;
    };
  }, [tab, emp.id, contracts]);

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="p-5 border-b border-stone-200 space-y-0">
        <div className="flex items-start gap-3">
          <div
            className={`w-12 h-12 rounded-full text-white text-sm font-bold flex items-center justify-center flex-shrink-0 ${avatarColor(emp.full_name)}`}
          >
            {initials(emp.full_name)}
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="font-serif text-xl text-stone-900">{emp.full_name}</SheetTitle>
            <div className="text-xs text-stone-500 font-mono mt-0.5">{emp.code}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${workStatus.cls}`}
              >
                {workStatus.l}
              </span>
              {emp.role && (
                <Badge variant="secondary" className="rounded-sm font-bold text-[10px] uppercase tracking-wider">
                  {ROLE_LABEL[emp.role] || emp.role}
                </Badge>
              )}
              {emp.level && (
                <span className="text-[10px] uppercase tracking-wider text-stone-600 font-bold">
                  {emp.level}
                </span>
              )}
              {emp.position && <span className="text-xs text-stone-600">· {emp.position}</span>}
            </div>
          </div>
        </div>
      </SheetHeader>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start rounded-none bg-stone-50 border-b border-stone-200 px-3 h-10">
          <TabsTrigger value="info" className="rounded-sm" data-testid="detail-tab-info">
            Thông tin
          </TabsTrigger>
          <TabsTrigger value="legal" className="rounded-sm" data-testid="detail-tab-legal">
            CCCD & NH
          </TabsTrigger>
          <TabsTrigger value="contracts" className="rounded-sm" data-testid="detail-tab-contracts">
            Hợp đồng
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-sm" data-testid="detail-tab-activity">
            Hoạt động
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="p-5 space-y-4">
          <DetailSection title="Liên hệ">
            <DetailRow icon={<Phone className="w-3.5 h-3.5" />} label="Số điện thoại" value={emp.phone || "—"} mono />
            <DetailRow icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={emp.email || "—"} mono />
            <DetailRow icon={<Cake className="w-3.5 h-3.5" />} label="Ngày sinh" value={emp.dob ? fmtDate(emp.dob) : "—"} />
            <DetailRow icon={<UsersIcon className="w-3.5 h-3.5" />} label="Giới tính" value={emp.gender === "male" ? "Nam" : emp.gender === "female" ? "Nữ" : emp.gender || "—"} />
            <DetailRow icon={<MapPin className="w-3.5 h-3.5" />} label="Địa chỉ" value={emp.address || "—"} />
          </DetailSection>
          <DetailSection title="Tổ chức">
            <DetailRow icon={<Building2 className="w-3.5 h-3.5" />} label="Pháp nhân" value={emp.company_name || "—"} />
            <DetailRow icon={<MapPin className="w-3.5 h-3.5" />} label="Chi nhánh" value={emp.branch_name || "—"} />
            <DetailRow icon={<Building2 className="w-3.5 h-3.5" />} label="Phòng ban" value={emp.department || "—"} />
            <DetailRow icon={<UsersIcon className="w-3.5 h-3.5" />} label="Quản lý trực tiếp" value={emp.manager_name ? `${emp.manager_name} (${emp.manager_code || ""})` : "—"} />
            <DetailRow icon={<Banknote className="w-3.5 h-3.5" />} label="Lương cơ bản" value={fmtMoney(emp.base_salary)} mono />
            <DetailRow icon={<CalendarDays className="w-3.5 h-3.5" />} label="Ngày vào · Thâm niên" value={`${emp.hire_date ? fmtDate(emp.hire_date) : "—"} · ${tenureText(emp.hire_date)}`} />
          </DetailSection>
          {emp.note && (
            <DetailSection title="Ghi chú">
              <div className="px-3 py-2 text-sm text-stone-700 whitespace-pre-wrap">{emp.note}</div>
            </DetailSection>
          )}
        </TabsContent>

        <TabsContent value="legal" className="p-5 space-y-4">
          <DetailSection title="CCCD">
            <DetailRow icon={<IdCard className="w-3.5 h-3.5" />} label="Số CCCD" value={emp.cccd || "—"} mono />
            <DetailRow icon={<CalendarDays className="w-3.5 h-3.5" />} label="Ngày cấp" value={emp.cccd_issue_date ? fmtDate(emp.cccd_issue_date) : "—"} />
            <DetailRow icon={<MapPin className="w-3.5 h-3.5" />} label="Nơi cấp" value={emp.cccd_issue_place || "—"} />
          </DetailSection>
          <DetailSection title="Ngân hàng">
            <DetailRow icon={<Landmark className="w-3.5 h-3.5" />} label="Ngân hàng" value={emp.bank_name || "—"} />
            <DetailRow icon={<Landmark className="w-3.5 h-3.5" />} label="Số tài khoản" value={emp.bank_account || "—"} mono />
            <DetailRow icon={<UsersIcon className="w-3.5 h-3.5" />} label="Chủ tài khoản" value={emp.bank_holder || emp.full_name || "—"} />
          </DetailSection>
        </TabsContent>

        <TabsContent value="contracts" className="p-5 space-y-3">
          <ContractsList contracts={contracts} error={contractsErr} />
        </TabsContent>

        <TabsContent value="activity" className="p-5 space-y-3">
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 text-xs text-stone-600">
            <div className="font-bold text-stone-800 text-sm mb-1">Lịch sử thay đổi</div>
            <p>
              Để xem chi tiết các thay đổi với hồ sơ này, mở trang{" "}
              <a href="/admin/audit-history" className="text-emerald-800 font-semibold hover:underline">
                Audit History
              </a>{" "}
              và lọc theo loại resource <span className="font-mono">employees</span> và mã{" "}
              <span className="font-mono">{emp.code}</span>.
            </p>
          </div>
          <div className="text-[11px] text-stone-500 font-mono space-y-1">
            <div>Created: {emp.created_at ? fmtDate(emp.created_at) : "—"}</div>
            <div>Version: {emp.version ?? "—"}</div>
            <div>Trạng thái: {deleted ? `Đã nghỉ (${fmtDate(emp.deleted_at)})` : "Đang làm"}</div>
          </div>
        </TabsContent>
      </Tabs>

      <SheetFooter className="p-4 border-t border-stone-200 flex sm:justify-between gap-2">
        <Button variant="outline" onClick={onClose} className="rounded-sm">
          Đóng
        </Button>
        <div className="flex gap-2">
          {!deleted ? (
            <>
              <Button onClick={onEdit} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="detail-edit">
                <Pencil className="w-4 h-4 mr-1.5" /> Sửa
              </Button>
              <DestructiveConfirmDialog
                title="Xóa nhân viên?"
                description={`Nhân viên ${emp.full_name} sẽ bị chuyển sang trạng thái đã nghỉ.`}
                confirmLabel="Xóa"
                token={String(emp.code)}
                onConfirm={onDelete}
                trigger={
                  <Button variant="outline" className="rounded-sm text-red-700 hover:text-red-800 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Xóa
                  </Button>
                }
              />
            </>
          ) : (
            <Button variant="outline" disabled className="rounded-sm">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Đã nghỉ
            </Button>
          )}
        </div>
      </SheetFooter>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold mb-2">{title}</div>
      <div className="border border-stone-200 rounded-sm divide-y divide-stone-100">{children}</div>
    </div>
  );
}

function DetailRow({ icon, label, value, mono, muted }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm">
      <span className="text-stone-400 flex-shrink-0">{icon}</span>
      <span className="text-stone-500 text-xs flex-1">{label}</span>
      <span className={`text-stone-900 text-right break-all ${mono ? "font-mono text-xs" : ""} ${muted ? "text-stone-500" : ""}`}>
        {value}
      </span>
    </div>
  );
}

const CONTRACT_TYPE_LABEL = {
  probation: "Thử việc",
  fixed_1y: "Chính thức 1 năm",
  fixed_2y: "Chính thức 2 năm",
  indefinite: "Vô thời hạn",
  seasonal: "Thời vụ",
  intern: "Thực tập",
};
const CONTRACT_STATUS_LABEL = {
  active: { l: "Đang hiệu lực", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  draft: { l: "Nháp", cls: "bg-stone-50 text-stone-700 border-stone-200" },
  expired: { l: "Hết hạn", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  terminated: { l: "Đã kết thúc", cls: "bg-red-50 text-red-800 border-red-200" },
  superseded: { l: "Đã thay thế", cls: "bg-violet-50 text-violet-800 border-violet-200" },
};

function ContractsList({ contracts, error }) {
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-800 rounded-sm p-3 text-sm">{error}</div>;
  }
  if (contracts === null) {
    return <div className="text-sm text-stone-500">Đang tải hợp đồng...</div>;
  }
  if (contracts.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-stone-300 rounded-sm">
        <FileSignature className="w-8 h-8 mx-auto text-stone-300 mb-2" />
        <div className="text-sm text-stone-500">Chưa có hợp đồng nào</div>
        <a href="/admin/contracts" className="text-emerald-800 text-xs font-semibold hover:underline mt-2 inline-block">
          + Tạo hợp đồng mới
        </a>
      </div>
    );
  }
  const active = contracts.find((c) => c.status === "active");
  return (
    <div className="space-y-3">
      {active && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-bold mb-1">HĐ đang hiệu lực</div>
          <div className="text-sm font-bold text-stone-900">
            {CONTRACT_TYPE_LABEL[active.contract_type] || active.contract_type}
            {active.code && <span className="ml-2 text-xs font-mono text-stone-500">{active.code}</span>}
          </div>
          <div className="text-xs text-stone-600 mt-1">
            {active.start_date ? fmtDate(active.start_date) : "—"} →{" "}
            {active.end_date ? fmtDate(active.end_date) : <span className="text-stone-400">Vô thời hạn</span>}
          </div>
          <div className="text-xs text-stone-600 font-mono mt-1">
            {fmtMoney(active.base_salary)}
            {active.allowance > 0 && ` + ${fmtMoney(active.allowance)} phụ cấp`}
          </div>
          {active.end_date && (() => {
            const days = Math.ceil((new Date(active.end_date) - Date.now()) / 86400000);
            if (days <= 30 && days >= 0)
              return (
                <div className="mt-2 text-xs text-amber-700 font-bold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Còn {days} ngày sẽ hết hạn
                </div>
              );
            if (days < 0)
              return (
                <div className="mt-2 text-xs text-red-700 font-bold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Đã quá hạn {-days} ngày
                </div>
              );
            return null;
          })()}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold mb-2">Lịch sử hợp đồng</div>
        <div className="border border-stone-200 rounded-sm divide-y divide-stone-100">
          {contracts.map((c) => {
            const def = CONTRACT_STATUS_LABEL[c.status] || CONTRACT_STATUS_LABEL.draft;
            return (
              <div key={c.id} className="px-3 py-2 flex items-start gap-3">
                <Clock4 className="w-3.5 h-3.5 text-stone-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-stone-900">
                      {CONTRACT_TYPE_LABEL[c.contract_type] || c.contract_type}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm ${def.cls}`}
                    >
                      {def.l}
                    </span>
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5">
                    {c.start_date ? fmtDate(c.start_date) : "—"} →{" "}
                    {c.end_date ? fmtDate(c.end_date) : "Vô thời hạn"}
                  </div>
                  <div className="text-[11px] text-stone-500 font-mono mt-0.5">{fmtMoney(c.base_salary)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <a
          href="/admin/contracts"
          className="text-emerald-800 text-xs font-semibold hover:underline mt-2 inline-block"
        >
          → Quản lý hợp đồng
        </a>
      </div>
    </div>
  );
}
